'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { getClient } from '@/lib/supabase/client';

const NOTIFICATION_REALTIME_EVENT = 'notification_changed';
const NOTIFICATION_REALTIME_REFRESH_DELAY_MS = 100;

function mergeUnique(current, incoming) {
  const ids = new Set(current.map((item) => item.id));
  return current.concat(incoming.filter((item) => !ids.has(item.id)));
}

async function requestJson(url, init) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    ...init,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error?.message || 'Notifications are temporarily unavailable.');
  return body;
}

export function useNotificationCenter({ enabled, authenticated, userId, open }) {
  const mountedRef = useRef(false);
  const openRef = useRef(open);
  const nextCursorRef = useRef(null);
  const unreadCountRef = useRef(0);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [featuredRewards, setFeaturedRewards] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const refreshUnread = useCallback(async () => {
    if (!enabled || !authenticated || !userId) return;
    try {
      const body = await requestJson('/api/notifications/unread-count');
      if (!mountedRef.current) return;
      const nextUnreadCount = Number(body.unreadCount || 0);
      unreadCountRef.current = nextUnreadCount;
      setUnreadCount(nextUnreadCount);

      const rewards = Array.isArray(body.featuredRewards)
        ? body.featuredRewards
        : body.featuredReward
          ? [body.featuredReward]
          : [];
      setFeaturedRewards(rewards.filter((reward) => (
        reward?.id && Number(reward?.payload?.rewardCredits || 0) > 0
      )));
    } catch {
      // A transient badge failure must not interrupt the app shell.
    }
  }, [authenticated, enabled, userId]);

  const load = useCallback(async ({ append = false } = {}) => {
    if (!enabled || !authenticated || !userId) return;
    setStatus(append ? 'loading-more' : 'loading');
    setError('');
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (append && nextCursorRef.current) params.set('cursor', nextCursorRef.current);
      const body = await requestJson(`/api/notifications?${params}`);
      if (!mountedRef.current) return;
      setItems((current) => append ? mergeUnique(current, body.items || []) : (body.items || []));
      nextCursorRef.current = body.nextCursor || null;
      setNextCursor(nextCursorRef.current);
      unreadCountRef.current = Number(body.unreadCount || 0);
      setUnreadCount(unreadCountRef.current);
      setStatus('ready');
    } catch (requestError) {
      if (!mountedRef.current) return;
      setError(requestError.message);
      setStatus('error');
    }
  }, [authenticated, enabled, userId]);

  useEffect(() => {
    if (!authenticated || !userId) {
      setItems([]);
      setUnreadCount(0);
      setFeaturedRewards([]);
      unreadCountRef.current = 0;
      nextCursorRef.current = null;
      setNextCursor(null);
      setStatus('idle');
      return;
    }
    void refreshUnread();
  }, [authenticated, refreshUnread, userId]);

  useEffect(() => {
    if (!authenticated || !enabled || !userId) return undefined;

    const supabase = getClient();
    if (!supabase?.realtime?.setAuth || !supabase?.channel || !supabase?.removeChannel) {
      return undefined;
    }

    let cancelled = false;
    let channel = null;
    let refreshTimer = null;
    let hasSubscribed = false;

    const syncFromServer = () => {
      if (cancelled) return;
      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        if (cancelled) return;
        void refreshUnread();
        if (openRef.current) void load();
      }, NOTIFICATION_REALTIME_REFRESH_DELAY_MS);
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') syncFromServer();
    };

    const subscribe = async () => {
      try {
        await supabase.realtime.setAuth();
        if (cancelled) return;

        channel = supabase
          .channel(`notifications:${userId}`, {
            config: { private: true },
          })
          .on('broadcast', { event: NOTIFICATION_REALTIME_EVENT }, syncFromServer)
          .subscribe((subscriptionStatus) => {
            if (subscriptionStatus !== 'SUBSCRIBED') return;
            if (hasSubscribed) syncFromServer();
            hasSubscribed = true;
          });
      } catch {
        // Initial HTTP state plus focus/online refreshes keep notifications
        // recoverable if Realtime is temporarily unavailable.
      }
    };

    void subscribe();
    window.addEventListener('focus', syncFromServer);
    window.addEventListener('online', syncFromServer);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      cancelled = true;
      if (refreshTimer) window.clearTimeout(refreshTimer);
      window.removeEventListener('focus', syncFromServer);
      window.removeEventListener('online', syncFromServer);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      if (channel) void supabase.removeChannel(channel);
    };
  }, [authenticated, enabled, load, refreshUnread, userId]);

  useEffect(() => {
    if (open && authenticated && userId) void load();
  }, [authenticated, load, open, userId]);

  const markRead = useCallback(async (id) => {
    const target = items.find((item) => item.id === id);
    if (target?.readAt) return;
    setFeaturedRewards((current) => current.filter((reward) => reward.id !== id));
    if (!target) {
      await requestJson(`/api/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' });
      await refreshUnread();
      return;
    }
    const readAt = new Date().toISOString();
    setItems((current) => current.map((item) => item.id === id ? { ...item, readAt } : item));
    setUnreadCount((count) => {
      const nextCount = Math.max(0, count - 1);
      unreadCountRef.current = nextCount;
      return nextCount;
    });
    try {
      await requestJson(`/api/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' });
    } catch {
      void load();
      void refreshUnread();
    }
  }, [items, load, refreshUnread]);

  const markAllRead = useCallback(async () => {
    if (!unreadCount) return;
    const readAt = new Date().toISOString();
    setItems((current) => current.map((item) => item.readAt ? item : { ...item, readAt }));
    unreadCountRef.current = 0;
    setUnreadCount(0);
    setFeaturedRewards([]);
    try {
      await requestJson('/api/notifications/read-all', { method: 'POST' });
    } catch {
      void load();
      void refreshUnread();
    }
  }, [load, refreshUnread, unreadCount]);

  return {
    items,
    unreadCount,
    featuredRewards,
    featuredReward: featuredRewards.at(-1) || null,
    nextCursor,
    status,
    error,
    load,
    loadMore: () => load({ append: true }),
    markRead,
    markAllRead,
  };
}
